/**
 * Permission utilities shared by:
 *   - CASL-driven UI gates (in Vue templates via $can)
 *   - Route guard (Routes.js beforeEach)
 *   - Empty-state rendering (EmptyState component restricted mode)
 *   - Post-login redirect (customer.js handleSelectSite)
 *
 * Centralizes the pipe-delimited-employee-list check and the human-readable
 * labels used in restricted-state copy. Mirror these changes in
 * cypress/node-tests/* test files that inline the same logic with
 * "KEEP IN SYNC" comments.
 */

// =============================================================================
// LABEL MAPS
// =============================================================================

// Subject (CASL feature key) → human-readable page name shown in UI copy.
// The first six are the follow-up types that also have responsible-list
// gating; the rest are other subjects that may appear in route-guard
// toast copy. Unknown keys fall back to the raw subject string.
export const SUBJECT_PAGE_LABELS = {
    'overdue_recall': 'Overdue Recall',
    'unscheduled_treatment': 'Unscheduled Treatment',
    'broken_appointment': 'Broken Appointments',
    'outstanding_claim': 'Outstanding Claims',
    'outstanding_preauths': 'Outstanding Pre-Authorizations',
    'patient_balances': 'Patient Balances',
    'team-insights': 'Team Insights',
    'huddle': 'Huddle',
    'kanban': 'Kanban',
    'tasks': 'Tasks',
    'wiki': 'Wiki',
    'forms': 'Forms',
    'campaigns': 'Campaigns',
    'insurance-verification': 'Insurance Verification',
    'practice-pulse': 'Practice Ops',
    'workflow': 'Workflow',
    'settings': 'Settings',
    'myaccount': 'My Account',
    'kpi': 'KPI',
    'conversations': 'Conversations',
    'sms-conversations': 'SMS Conversations',
    'chat': 'Chat',
    'online-appointments': 'Online Appointments',
    'apt-reminder': 'Appointment Reminder',
    'ai-call-history': 'Bot Conversations',
    'schedule': 'Schedule - Chart Audit',
};

// Subject → name of the *_RESPONSIBLE_EMPLOYEEID setting (as it appears in
// Settings UI). Used in restricted-state copy so the user knows the exact
// setting their admin needs to update.
export const SUBJECT_RESPONSIBLE_LABELS = {
    'overdue_recall': 'Overdue Recall responsible employees',
    'unscheduled_treatment': 'Unscheduled Treatment responsible employees',
    'broken_appointment': 'Broken Appointments responsible employees',
    'outstanding_claim': 'Outstanding Claims responsible employees',
    'outstanding_preauths': 'Outstanding Pre-Authorizations responsible employees',
    'patient_balances': 'Patient Balances responsible employees',
};

// Subject → the actual settings key (the *_RESPONSIBLE_EMPLOYEEID
// PropertyKey in the `settings` table). Used by callers that need to read
// the raw setting value for membership checks.
export const SUBJECT_RESPONSIBLE_SETTING_KEYS = {
    'overdue_recall': 'OVERDUE_RECALL_RESPONSIBLE_EMPLOYEEID',
    'unscheduled_treatment': 'UNSCHEDULED_TREATMENT_RESPONSIBLE_EMPLOYEEID',
    'broken_appointment': 'BROKEN_APPOINTMENTS_RESPONSIBLE_EMPLOYEEID',
    'outstanding_claim': 'OUTSTANDING_CLAIM_RESPONSIBLE_EMPLOYEEID',
    'outstanding_preauths': 'OUTSTANDING_PREAUTHS_RESPONSIBLE_EMPLOYEEID',
    'patient_balances': 'PATIENT_BALANCES_RESPONSIBLE_EMPLOYEEID',
};

// =============================================================================
// CORE HELPERS
// =============================================================================

/**
 * Checks whether `employeeId` appears in a pipe-delimited PropertyValue
 * like "empA|empB|empC". Replaces broken `===` comparisons that only
 * worked for single-employee setups.
 *
 * @param {string} employeeId
 * @param {string} propertyValue - pipe-delimited string of EmployeeIDs
 * @returns {boolean}
 */
export function isInResponsibleList(employeeId, propertyValue) {
    if (!employeeId || typeof employeeId !== 'string') return false;
    if (!propertyValue || typeof propertyValue !== 'string') return false;
    return propertyValue.split('|').map(s => s.trim()).includes(employeeId);
}

/**
 * Splits a pipe-delimited *_RESPONSIBLE_EMPLOYEEID PropertyValue into
 * individual EmployeeIDs. Returns an empty array on null/undefined/empty
 * input. Used by Settings.vue to push individual IDs to backend filters
 * that expect a flat array (previously pushed the whole pipe-string as
 * one ID, breaking every multi-employee site's filter).
 *
 * @param {string} propertyValue
 * @returns {string[]}
 */
export function splitResponsibleEmployeeIds(propertyValue) {
    if (!propertyValue || typeof propertyValue !== 'string') return [];
    return propertyValue.split('|').map(s => s.trim()).filter(Boolean);
}

/**
 * Resolves a pipe-delimited *_RESPONSIBLE_EMPLOYEEID PropertyValue into
 * the names of every matching employee, ordered the way the PropertyValue
 * lists them (so "Alice|Bob|Carol" → first=Alice, not whoever happens to
 * be first in the local employees array).
 *
 * Powers the "first name + N more" badge in AdminPage, AdminPage2, and
 * AdminByEmployeePage section headers. Replaces the old per-page code that
 * used `===` against the whole pipe-string and returned only `employees[0]`
 * — broken for multi-employee setups AND misleading even when it worked.
 *
 * Returns null when nothing resolves (no employees, no PropertyValue, or
 * none of the listed IDs exist in the employees array). Templates render
 * the badge under `v-if="responsible"`.
 *
 * @param {Array<{EmployeeID: string, Name: string}>} employees
 * @param {string} propertyValue - pipe-delimited EmployeeIDs
 * @returns {{first: string, all: string[], count: number} | null}
 */
export function getResponsibleEmployees(employees, propertyValue) {
    if (!Array.isArray(employees) || employees.length === 0) return null;
    if (!propertyValue || typeof propertyValue !== 'string') return null;

    const ids = propertyValue.split('|').map(s => s.trim()).filter(Boolean);
    if (ids.length === 0) return null;

    const byId = new Map();
    employees.forEach(e => {
        if (e && e.EmployeeID) byId.set(e.EmployeeID, e);
    });

    const names = [];
    for (const id of ids) {
        const emp = byId.get(id);
        if (emp && emp.Name) names.push(emp.Name);
    }

    if (names.length === 0) return null;
    return { first: names[0], all: names, count: names.length };
}

/**
 * Decides which empty-state mode an empty card/page section should render.
 *
 * @param {object} ability   - CASL Ability instance (has .can(action, subject))
 * @param {string} subject   - feature key e.g. 'overdue_recall'
 * @param {number} dataCount - items in this section (treated as 0 if falsy/negative)
 * @returns {'has-data' | 'access-restricted' | 'truly-empty'}
 *
 * Resolution order:
 *   1. Data wins. If dataCount > 0 → 'has-data' (caller renders the chart/list).
 *   2. No ability available → 'truly-empty' (don't lock the UI on cold-boot).
 *   3. Ability denies view → 'access-restricted' (show restricted message).
 *   4. Otherwise → 'truly-empty' (current "No Data Available" state).
 */
export function getEmptyStateMode(ability, subject, dataCount) {
    if (typeof dataCount === 'number' && dataCount > 0) return 'has-data';
    if (!ability || typeof ability.can !== 'function') return 'truly-empty';
    if (!ability.can('view', subject)) return 'access-restricted';
    return 'truly-empty';
}

/**
 * Returns the human-readable page label for a subject, falling back to
 * the raw subject string for unknown keys.
 */
export function getPageLabel(subject) {
    return SUBJECT_PAGE_LABELS[subject] || subject;
}

/**
 * Returns the human-readable responsible-list label for a subject, or
 * null if this subject isn't gated by a *_RESPONSIBLE_EMPLOYEEID setting
 * (e.g. campaigns, workflow, forms).
 */
export function getResponsibleListLabel(subject) {
    return SUBJECT_RESPONSIBLE_LABELS[subject] || null;
}

/**
 * User-facing copy for the "Access Restricted" empty state.
 * Names the specific responsible-list setting so the user has an actionable
 * ask for their admin. Falls back to generic copy for subjects that aren't
 * gated by a responsible-list setting.
 */
export function getRestrictedMessage(subject) {
    const listLabel = getResponsibleListLabel(subject);
    if (listLabel) {
        return `Ask your admin to add you to the ${listLabel} in Settings.`;
    }
    return `Your role doesn't include access to ${getPageLabel(subject)}. Ask your admin.`;
}

/**
 * Toast message dispatched from the router beforeEach guard when navigation
 * is denied by the meta.requires CASL check. Differs from
 * getRestrictedMessage() by leading with the "Access denied to <page>"
 * phrasing — the user just attempted a navigation and we tell them why
 * it bounced.
 */
export function getRouteGuardToastMessage(subject) {
    const listLabel = getResponsibleListLabel(subject);
    const pageLabel = getPageLabel(subject);
    if (listLabel) {
        return `Access denied to ${pageLabel}. Ask your admin to add you to the ${listLabel} in Settings.`;
    }
    return `Access denied to ${pageLabel}. Ask your admin to grant your role access.`;
}
